import type { useApiFetch } from '@/features/auth'
import { buildQueryString } from '@/lib/buildQueryString'
import type {
  CallOutcome,
  CallOutcomeCount,
  CallSummary,
  CaseBuildProfile,
  CaseDetail,
  CaseDocumentDownload,
  CaseDocumentPasswordResult,
  CaseDocumentSummary,
  CaseGoal,
  CaseHolding,
  CasePrescriptionStep,
  CaseStatus,
  CaseStatusCount,
  CaseSummary,
  CaseTrade,
  CaseUploadLinkCreated,
  CaseUploadLinkSummary,
  CreateCaseGoalFundInput,
  CreateCaseGoalInput,
  CreateCaseHoldingInput,
  CreateCasePrescriptionStepInput,
  CreateCaseTradeInput,
  DialCallResult,
  SaveInvestorProfileInput,
  SendInvestorLoginDetailsResult,
  SeriousnessStatus,
  ServiceType,
  UpdateCallOutcomeInput,
  UpdateCaseGoalFundInput,
  UpdateCaseGoalInput,
  UpdateCaseHoldingInput,
  UpdateCasePrescriptionStepInput,
  UpdateCaseTradeInput,
  UpsertCaseBuildProfileInput,
} from '@/features/cases/types'

type AuthedFetch = ReturnType<typeof useApiFetch>

/** `GET /cases` query params (`ListCasesQueryDto`). `advisorId`/`unassigned` are admin-only — the
 * backend silently ignores them for a staff caller. `scope` is staff-only (ignored for admin, who
 * already has `unassigned` above) — `'unassigned'` requests the self-claim queue instead of the
 * caller's own assigned cases (the default, `'mine'`, same behavior as omitting it entirely). */
export interface CasesListParams {
  page?: number
  pageSize?: number
  search?: string
  status?: CaseStatus
  serviceType?: ServiceType
  seriousness?: SeriousnessStatus
  sort?: 'newest' | 'oldest'
  advisorId?: string
  unassigned?: boolean
  scope?: 'mine' | 'unassigned'
}

export interface CasesListResponse {
  items: CaseSummary[]
  page: number
  pageSize: number
  total: number
  totalPages: number
  counts: CaseStatusCount[]
}

export function fetchCases(
  authedFetch: AuthedFetch,
  params: CasesListParams = {},
): Promise<CasesListResponse> {
  return authedFetch<CasesListResponse>(`/api/v1/cases${buildQueryString(params)}`)
}

export function fetchCaseDetail(authedFetch: AuthedFetch, caseId: string): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}`)
}

/** Mirrors `CreateCaseDto` (Add Lead). `build_portfolio` creates a bare case with no goals/
 * profile yet — staff fills those in afterward via the case-detail Goals tab. */
export interface CreateCaseInput {
  serviceType: ServiceType
  name: string
  email: string
  mobile: string
  pan?: string
  city?: string
  seriousnessStatus?: SeriousnessStatus
}

export function createCase(authedFetch: AuthedFetch, input: CreateCaseInput): Promise<CaseDetail> {
  return authedFetch<CaseDetail>('/api/v1/cases', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Mirrors `UpdateCaseDto` — all optional; the caller sends only the fields it means to change. */
export interface UpdateCaseInput {
  note?: string
  nextFollowUpAt?: string
  wealthScore?: number
}

export function updateCase(
  authedFetch: AuthedFetch,
  caseId: string,
  input: UpdateCaseInput,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function updateCaseStatus(
  authedFetch: AuthedFetch,
  caseId: string,
  status: CaseStatus,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

export function updateCaseSeriousness(
  authedFetch: AuthedFetch,
  caseId: string,
  seriousnessStatus: SeriousnessStatus,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/seriousness`, {
    method: 'PATCH',
    body: JSON.stringify({ seriousnessStatus }),
  })
}

/** Mirrors `AssignAdvisorDto` — the field is `staffId`, not `advisorId`, despite the route name. */
export function assignCaseAdvisor(
  authedFetch: AuthedFetch,
  caseId: string,
  staffId: string,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/advisor`, {
    method: 'PUT',
    body: JSON.stringify({ staffId }),
  })
}

export function unassignCaseAdvisor(authedFetch: AuthedFetch, caseId: string): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/advisor`, { method: 'DELETE' })
}

/** Staff self-claim of an unassigned case — a separate route from `assignCaseAdvisor` (admin-only). */
export function claimCaseAdvisor(authedFetch: AuthedFetch, caseId: string): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/claim`, { method: 'POST' })
}

export function saveInvestorProfile(
  authedFetch: AuthedFetch,
  caseId: string,
  input: SaveInvestorProfileInput,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/investor-account`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function publishCase(authedFetch: AuthedFetch, caseId: string): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/publish`, { method: 'POST' })
}

export function sendCaseLoginDetails(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<SendInvestorLoginDetailsResult> {
  return authedFetch<SendInvestorLoginDetailsResult>(`/api/v1/cases/${caseId}/send-login-details`, {
    method: 'POST',
  })
}

export function fetchCaseHoldings(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<CaseHolding[]> {
  return authedFetch<CaseHolding[]>(`/api/v1/cases/${caseId}/holdings`)
}

/** Build Portfolio only — returns `[]` for the other 2 service types (no goals ever exist there). */
export function fetchCaseGoals(authedFetch: AuthedFetch, caseId: string): Promise<CaseGoal[]> {
  return authedFetch<CaseGoal[]>(`/api/v1/cases/${caseId}/goals`)
}

export interface CaseTradesPage {
  items: CaseTrade[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export function fetchCaseTrades(
  authedFetch: AuthedFetch,
  caseId: string,
  params: { page?: number; pageSize?: number } = {},
): Promise<CaseTradesPage> {
  return authedFetch<CaseTradesPage>(`/api/v1/cases/${caseId}/trades${buildQueryString(params)}`)
}

export function fetchCaseSnapshots(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<{ id: string; asOf: string; investedValue: string; currentValue: string }[]> {
  return authedFetch(`/api/v1/cases/${caseId}/snapshots`)
}

export interface CaseEvent {
  id: string
  type: string
  actorId: string | null
  actorName: string | null
  fromValue: string | null
  toValue: string | null
  createdAt: string
}

export interface CaseEventsPage {
  items: CaseEvent[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export function fetchCaseEvents(
  authedFetch: AuthedFetch,
  caseId: string,
  params: { page?: number; pageSize?: number } = {},
): Promise<CaseEventsPage> {
  return authedFetch<CaseEventsPage>(`/api/v1/cases/${caseId}/events${buildQueryString(params)}`)
}

export function fetchCasePreview(authedFetch: AuthedFetch, caseId: string): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/preview`)
}

/** `GET /calls` query params (`ListCallsQueryDto`). `advisorId` is admin-only — the backend
 * silently ignores it for a staff caller, who is always implicitly scoped to their own calls. */
export interface CallsListParams {
  page?: number
  pageSize?: number
  /** A real `CallOutcome`, or `AWAITING_NOTES_VALUE` (`'awaiting_notes'`) to filter to calls with
   * no outcome recorded yet — a URL param can't carry a literal `null`. */
  outcome?: CallOutcome | 'awaiting_notes'
  search?: string
  advisorId?: string
}

export interface CallsListResponse {
  items: CallSummary[]
  page: number
  pageSize: number
  total: number
  totalPages: number
  // Computed over the entire filtered scope (same `where` as `items`, before pagination), not
  // just the current page — a staff member's "12 connected of 340 total" is correct even while
  // only one page of rows renders.
  counts: CallOutcomeCount[]
  totalTalkTimeMinutes: number
  withRecordingCount: number
}

export function fetchCalls(
  authedFetch: AuthedFetch,
  params: CallsListParams = {},
): Promise<CallsListResponse> {
  return authedFetch<CallsListResponse>(`/api/v1/calls${buildQueryString(params)}`)
}

// ---------------------------------------------------------------------------
// Case sub-resource writes — Holdings/Trades/Prescription steps/Calls. All staff-only
// (`@Roles('staff')` on the backend); admin stays read-only on every one of these.
// ---------------------------------------------------------------------------

export function createCaseHolding(
  authedFetch: AuthedFetch,
  caseId: string,
  input: CreateCaseHoldingInput,
): Promise<CaseHolding> {
  return authedFetch<CaseHolding>(`/api/v1/cases/${caseId}/holdings`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function deleteCaseHolding(
  authedFetch: AuthedFetch,
  caseId: string,
  holdingId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/holdings/${holdingId}`, { method: 'DELETE' })
}

export function updateCaseHolding(
  authedFetch: AuthedFetch,
  caseId: string,
  holdingId: string,
  input: UpdateCaseHoldingInput,
): Promise<CaseHolding> {
  return authedFetch<CaseHolding>(`/api/v1/cases/${caseId}/holdings/${holdingId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** `PUT /cases/:caseId/holdings` — the whole sheet is retyped/re-pasted, not edited row by row: the
 * body is a bare array (never `{ holdings: [...] }`), and every item needs the full
 * `CreateCaseHoldingInput` shape since the backend deletes the old set and creates the new one. */
export function replaceCaseHoldings(
  authedFetch: AuthedFetch,
  caseId: string,
  inputs: CreateCaseHoldingInput[],
): Promise<CaseHolding[]> {
  return authedFetch<CaseHolding[]>(`/api/v1/cases/${caseId}/holdings`, {
    method: 'PUT',
    body: JSON.stringify(inputs),
  })
}

export function createCaseTrade(
  authedFetch: AuthedFetch,
  caseId: string,
  input: CreateCaseTradeInput,
): Promise<CaseTrade> {
  return authedFetch<CaseTrade>(`/api/v1/cases/${caseId}/trades`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function deleteCaseTrade(
  authedFetch: AuthedFetch,
  caseId: string,
  tradeId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/trades/${tradeId}`, { method: 'DELETE' })
}

export function updateCaseTrade(
  authedFetch: AuthedFetch,
  caseId: string,
  tradeId: string,
  input: UpdateCaseTradeInput,
): Promise<CaseTrade> {
  return authedFetch<CaseTrade>(`/api/v1/cases/${caseId}/trades/${tradeId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** `PUT /cases/:caseId/trades` — the whole tradebook is retyped/re-pasted, not edited row by row:
 * the body is a bare array (never `{ trades: [...] }`), and every item needs the full
 * `CreateCaseTradeInput` shape since the backend deletes the old set and creates the new one. */
export function replaceCaseTrades(
  authedFetch: AuthedFetch,
  caseId: string,
  inputs: CreateCaseTradeInput[],
): Promise<CaseTrade[]> {
  return authedFetch<CaseTrade[]>(`/api/v1/cases/${caseId}/trades`, {
    method: 'PUT',
    body: JSON.stringify(inputs),
  })
}

export function createCaseGoal(
  authedFetch: AuthedFetch,
  caseId: string,
  input: CreateCaseGoalInput,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateCaseGoal(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
  input: UpdateCaseGoalInput,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals/${goalId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** Dedicated route (not folded into `updateCaseGoal`) — toggles `planStatus` between
 * `approved`/`pending` without touching the plan's own fields. */
export function setCaseGoalPlanApproved(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
  approved: boolean,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals/${goalId}/approve`, {
    method: 'PATCH',
    body: JSON.stringify({ approved }),
  })
}

export function deleteCaseGoal(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/goals/${goalId}`, { method: 'DELETE' })
}

/** A goal can have several funds — these 3 mirror `createCaseGoal`/`updateCaseGoal`/
 * `deleteCaseGoal`'s exact shape, one dedicated route per fund-plan mutation. Each returns the
 * updated goal (not just the fund) since adding/editing/removing a fund also changes the parent
 * goal's computed `totalMonthlySip`/`planStatus`. */
export function addCaseGoalFund(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
  input: CreateCaseGoalFundInput,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals/${goalId}/funds`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateCaseGoalFund(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
  fundId: string,
  input: UpdateCaseGoalFundInput,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals/${goalId}/funds/${fundId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteCaseGoalFund(
  authedFetch: AuthedFetch,
  caseId: string,
  goalId: string,
  fundId: string,
): Promise<CaseGoal> {
  return authedFetch<CaseGoal>(`/api/v1/cases/${caseId}/goals/${goalId}/funds/${fundId}`, {
    method: 'DELETE',
  })
}

/** `PUT /cases/:caseId/build-profile` — create-or-update; an Add-Lead-created Build Portfolio case
 * has no profile row yet, so this must be able to create the first one too. */
export function upsertCaseBuildProfile(
  authedFetch: AuthedFetch,
  caseId: string,
  input: UpsertCaseBuildProfileInput,
): Promise<CaseBuildProfile> {
  return authedFetch<CaseBuildProfile>(`/api/v1/cases/${caseId}/build-profile`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function createCasePrescriptionStep(
  authedFetch: AuthedFetch,
  caseId: string,
  input: CreateCasePrescriptionStepInput,
): Promise<CasePrescriptionStep> {
  return authedFetch<CasePrescriptionStep>(`/api/v1/cases/${caseId}/prescription-steps`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateCasePrescriptionStep(
  authedFetch: AuthedFetch,
  caseId: string,
  stepId: string,
  input: UpdateCasePrescriptionStepInput,
): Promise<CasePrescriptionStep> {
  return authedFetch<CasePrescriptionStep>(`/api/v1/cases/${caseId}/prescription-steps/${stepId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** Investor-only counterpart to `updateCasePrescriptionStep` — can only ever toggle `done`, never
 * edit text/priority/points/position. Hits a distinct endpoint (`.../done`), never the general
 * staff PATCH. */
export function setMyCasePrescriptionStepDone(
  authedFetch: AuthedFetch,
  caseId: string,
  stepId: string,
  done: boolean,
): Promise<CasePrescriptionStep> {
  return authedFetch<CasePrescriptionStep>(
    `/api/v1/cases/${caseId}/prescription-steps/${stepId}/done`,
    { method: 'PATCH', body: JSON.stringify({ done }) },
  )
}

export function deleteCasePrescriptionStep(
  authedFetch: AuthedFetch,
  caseId: string,
  stepId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/prescription-steps/${stepId}`, {
    method: 'DELETE',
  })
}

/** Click-to-call: rings the assigned staff member first, then bridges to the investor once they
 * answer (`Case.contactMobile`, chosen server-side — no body). */
export function dialCase(authedFetch: AuthedFetch, caseId: string): Promise<DialCallResult> {
  return authedFetch<DialCallResult>(`/api/v1/cases/${caseId}/calls/dial`, { method: 'POST' })
}

/** The live-call polling target — same shape as one of `fetchCalls`' rows. */
export function fetchCallStatus(authedFetch: AuthedFetch, callId: string): Promise<CallSummary> {
  return authedFetch<CallSummary>(`/api/v1/calls/${callId}/status`)
}

/** The "wrap up this call" submission for a real Plivo call — updates the same row, never
 * creates a new one. */
export function updateCallOutcome(
  authedFetch: AuthedFetch,
  callId: string,
  input: UpdateCallOutcomeInput,
): Promise<CallSummary> {
  return authedFetch<CallSummary>(`/api/v1/calls/${callId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

/** Notes-only edit — applies to both a real Plivo call and a manually-logged one, distinct from
 * `updateCallOutcome` (which requires an `outcome` and only fits a Plivo wrap-up). */
export function updateCallNotes(
  authedFetch: AuthedFetch,
  callId: string,
  notes: string,
): Promise<CallSummary> {
  return authedFetch<CallSummary>(`/api/v1/calls/${callId}/notes`, {
    method: 'PATCH',
    body: JSON.stringify({ notes }),
  })
}

// ---------------------------------------------------------------------------
// Documents & upload-links — staff-only. The upload POST itself does not go through
// `authedFetch`/`apiFetch` — it goes through `useFileUpload()`'s XHR-based `upload()`
// (`hooks/use-case-document-writes.ts`), which needs progress events `fetch` can't provide.
// ---------------------------------------------------------------------------

export function deleteCaseDocument(
  authedFetch: AuthedFetch,
  caseId: string,
  documentId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/documents/${documentId}`, {
    method: 'DELETE',
  })
}

export function fetchCaseDocumentDownload(
  authedFetch: AuthedFetch,
  caseId: string,
  documentId: string,
): Promise<CaseDocumentDownload> {
  return authedFetch<CaseDocumentDownload>(
    `/api/v1/cases/${caseId}/documents/${documentId}/download`,
  )
}

export function fetchCaseDocumentPassword(
  authedFetch: AuthedFetch,
  caseId: string,
  documentId: string,
): Promise<CaseDocumentPasswordResult> {
  return authedFetch<CaseDocumentPasswordResult>(
    `/api/v1/cases/${caseId}/documents/${documentId}/password`,
  )
}

export function createCaseUploadLink(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<CaseUploadLinkCreated> {
  return authedFetch<CaseUploadLinkCreated>(`/api/v1/cases/${caseId}/upload-links`, {
    method: 'POST',
  })
}

export function fetchCaseUploadLinks(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<CaseUploadLinkSummary[]> {
  return authedFetch<CaseUploadLinkSummary[]>(`/api/v1/cases/${caseId}/upload-links`)
}

export function revokeCaseUploadLink(
  authedFetch: AuthedFetch,
  caseId: string,
  linkId: string,
): Promise<void> {
  return authedFetch<void>(`/api/v1/cases/${caseId}/upload-links/${linkId}`, {
    method: 'DELETE',
  })
}

/**
 * The API seam for cases. Every function here is async and takes exactly
 * the arguments a real endpoint would need — swapping the body for a
 * `fetch()` call is the only change required to point this at a real
 * backend. Nothing above this layer (hooks, components) talks to the mock
 * database directly.
 */

/** Re-picking a consultation slot on an already-existing self-service case (the initial slot is
 * submitted together with `POST /cases/mine` itself — see `useCreateOwnCase`, which needs the
 * XHR-based `useFileUpload`, not this `authedFetch`-based seam, since that call also carries the
 * document). Re-picking here just updates the timestamp; the case's status was already advanced
 * to `consultation_scheduled` at creation. */
export function setOwnConsultationSlot(
  authedFetch: AuthedFetch,
  caseId: string,
  consultationSlotAt: string,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>(`/api/v1/cases/${caseId}/consultation-slot`, {
    method: 'PATCH',
    body: JSON.stringify({ consultationSlotAt }),
  })
}

/** Mirrors `CreateOwnBuildPortfolioCaseDto` — `mobile`/`clientDetails` are deliberately absent
 * (unlike the public wizard's equivalent payload): the backend resolves both from the caller's own
 * `Investor`/session, matching `CreateOwnCaseInput`'s existing convention of never trusting
 * client-supplied identity once a session exists. */
export interface CreateOwnBuildPortfolioCaseInput {
  consultationSlotAt: string
  age?: number
  expenses: {
    monthlyIncome: string
    rentEmi: string
    utilitiesGroceries: string
    transportLifestyle: string
    otherExpenses: string
  }
  goals: CreateCaseGoalInput[]
}

/** The logged-in investor's self-service "Build a plan" submission — `POST
 * /cases/mine/build-portfolio`, plain JSON (no document, unlike `useCreateOwnCase`'s multipart
 * audit/trades equivalent). Expenses and goals are both accepted in this single call — the backend
 * persists the case, `BuildPortfolioProfile`, and every goal in one transaction, so there's no
 * follow-up "add goals" round trip needed. */
export function createOwnBuildPortfolioCase(
  authedFetch: AuthedFetch,
  input: CreateOwnBuildPortfolioCaseInput,
): Promise<CaseDetail> {
  return authedFetch<CaseDetail>('/api/v1/cases/mine/build-portfolio', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function fetchCasePrescriptionSteps(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<CasePrescriptionStep[]> {
  return authedFetch<CasePrescriptionStep[]>(`/api/v1/cases/${caseId}/prescription-steps`)
}

export function fetchCaseDocumentList(
  authedFetch: AuthedFetch,
  caseId: string,
): Promise<CaseDocumentSummary[]> {
  return authedFetch<CaseDocumentSummary[]>(`/api/v1/cases/${caseId}/documents`)
}
